import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EnvelopeIcon, PhoneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/20/solid'
import {
	AtSymbolIcon,
	CalendarDaysIcon,
	ClockIcon,
	DevicePhoneMobileIcon,
	IdentificationIcon,
	MapPinIcon,
	MegaphoneIcon,
	UserCircleIcon,
	UsersIcon,
} from '@heroicons/react/24/outline'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import CustomerService from '@/services/CustomerService'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import { useNotifications } from '@/hooks/useNotifications'

const fmt = (d, pat = 'YYYY-MM-DD') => (d ? moment(d).format(pat) : '—')

export default function CustomerDetails({ customerId, customerDetails }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [deleting, setDeleting] = useState(false)
	const [error, setError] = useState(null)

	const { showDeleteConfirmation, showDeleteSuccess } = useNotifications({
		error,
		message: null,
		clearError: () => setError(null),
		clearMessage: () => {},
	})

	const handleEdit = () => {
		if (!customerId) return
		navigate(`/system/customers/edit/${customerId}`)
	}

	const handleDelete = async () => {
		if (!customerId || !customerDetails?._id) return
		const customerName = customerDetails?.name || t('customers.details.messages.deleteTargetFallback')
		const confirmed = await showDeleteConfirmation(1, t('customers.details.messages.deleteConfirm', { name: customerName }))
		if (!confirmed) return

		setDeleting(true)
		try {
			const { success, error: deleteError } = await CustomerService.deleteCustomers([customerDetails._id])
			if (!success) throw new Error(deleteError || t('customers.details.messages.deleteFailed'))
			navigate('/system/customers', {state: { message: t('customers.details.messages.deletedSuccess', { name: customerName }) }})
		} catch (err) {
			setError(err.message || t('customers.details.messages.deleteUnexpected'))
		} finally {
			setDeleting(false)
		}
	}

	if (!customerId) {
		return (
			<li className="col-span-1 xl:col-span-2 rounded-lg border border-dashed border-gray-300 bg-white/60 p-6 text-sm text-gray-500">
				{t('customers.details.selectPrompt')}
			</li>
		)
	}

	if (!customerDetails) {
		return <LoadingIndicator message={t('customers.details.loadingCustomer')} />
	}

	const customer = customerDetails
	const person = { ...customer }
	const linkedLeadId = customer?.leadId || customer?.lead?._id || customer?.lead?.id

	const DetailRow = ({ icon: Icon, label, children }) => (
		<div className="py-2 flex items-start gap-3">
			<Icon aria-hidden="true" className="size-5 text-gray-400 mt-0.5" />
			<div className="w-full sm:flex sm:items-start sm:gap-3">
				<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none">{label}</dt>
				<dd className="mt-1 text-gray-900 sm:mt-0 flex-1">{children}</dd>
			</div>
		</div>
	)

	return (
		<li className="col-span-1 xl:col-span-1 rounded-md bg-white shadow-sm">
			{/* Header */}
			<div className="flex w-full items-center justify-between space-x-6 p-6 py-4">
				<div className="flex-1 truncate">
					<div className="flex items-center space-x-3">
						<h2 className="truncate text-lg text-gray-900 font-semibold">{person.name}</h2>
					</div>
				</div>
				<div className="flex items-center justify-end">
					<div className="flex size-12 shrink-0 items-center justify-center">
						<UserCircleIcon aria-hidden="true" className="size-10 text-indigo-800" />
					</div>
				</div>
			</div>

			{/* Profile data */}
			<dl className="mt-2 px-6 text-sm/6">

				<DetailRow icon={UserCircleIcon} label={t('customers.details.labels.fullName')}>{(person.title ? `${person.title} ${person.name}` : person.name) || '—'}</DetailRow>
				<DetailRow icon={DevicePhoneMobileIcon} label={t('customers.details.labels.phone')}>{person.tel || '—'}</DetailRow>
				<DetailRow icon={MapPinIcon} label={t('customers.details.labels.address')}>{person.address || '—'}</DetailRow>
				<DetailRow icon={AtSymbolIcon} label={t('customers.details.labels.emailAddress')}>{person.email || '—'}</DetailRow>

				{person.sex && (
					<DetailRow icon={UsersIcon} label={t('customers.details.labels.gender')}><span className="capitalize">{person.sex}</span></DetailRow>
				)}

				<DetailRow icon={CalendarDaysIcon} label={t('customers.details.labels.dateOfBirth')}>{fmt(person.dob)}</DetailRow>

				{!linkedLeadId && (
					<DetailRow icon={MegaphoneIcon} label={t('customers.details.labels.source')}>{person.source || '—'}</DetailRow>
				)}
				{linkedLeadId && (
					<DetailRow icon={IdentificationIcon} label={t('customers.details.labels.lead')}>
						<button type="button" onClick={() => navigate(`/leads/details/${linkedLeadId}`)} className="text-indigo-600 hover:text-indigo-800 font-medium">
							{t('customers.details.actions.viewLead', { id: linkedLeadId })}
						</button>
					</DetailRow>
				)}
				<DetailRow icon={ClockIcon} label={t('customers.details.labels.created')}>{fmt(person.created, 'YYYY-MM-DD HH:mm')}</DetailRow>
			</dl>

			{/* Actions */}
			<div className="flex gap-3 px-6 pt-3 pb-6 justify-center">
				<button
					type="button"
					onClick={handleEdit}
					className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700">
					{t('customers.details.actions.edit')}
				</button>
				<button
					type="button"
					onClick={handleDelete}
					disabled={deleting}
					className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm ${
						deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
					}`}
				>
					{deleting ? t('customers.details.actions.deleting') : t('customers.details.actions.delete')}
				</button>
			</div>

			{/* Quick contact */}
			<div>
				<div className="-mt-px grid grid-cols-3 overflow-hidden rounded-b-lg border-t border-gray-200 bg-white divide-x divide-gray-200">
					<a
						href={`tel:${person.tel}`}
						className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-sky-800 transition hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
					>
						<PhoneIcon aria-hidden="true" className="size-5 text-sky-600" />
						<span>{t('customers.details.actions.call')}</span>
					</a>

					<a
						href={`https://wa.me/${person.tel}`}
						className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-sky-800 transition hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
					>
						<ChatBubbleLeftRightIcon aria-hidden="true" className="size-5 text-sky-600" />
						<span>{t('customers.details.actions.whatsapp')}</span>
					</a>

					<a
						href={`mailto:${person.email}`}
						className="flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold text-sky-800 transition hover:bg-sky-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
					>
						<EnvelopeIcon aria-hidden="true" className="size-5 text-sky-600" />
						<span>{t('customers.details.actions.email')}</span>
					</a>
				</div>
			</div>
		</li>
	)
}
